import { memo, useCallback, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import TvPressable from '../../../../components/tv_pressable';
import { commonStyles } from '../../../../common/styles';
import { MainStackProps } from '../../../../types/screens';
import useGlobalStyles from '../../../../common/style_context';
import AppColors from '../../../../common/colors';
import { observer } from 'mobx-react-lite';
import { FocusStateType, useFocusState } from '../../../../common/hooks';
import { computed } from 'mobx';
import { OptionsType } from '../../../../common/screen_params';
import Constants from '../../../../utils/constants';
import Utils from '../../../../utils/utils';
import KeyWrapper from '../../../../components/key_wrapper';
import { persistedStorage } from '../../../../common/storage';
import { VoidCb } from '../../../../types/others';
import localization from '../../../../common/localization';
import States from '../../../../common/states';
import { useTvToast } from '../../../../components/tv_toast/toast_provider';

type Props = MainStackProps<'OptionsDialogTv'>;

const dialogStyles = [
  `w=500,bg=${AppColors.containerGray},p=24,br=14`,
  'fs=32,fw=700,c=white,tc,mb=36',
];

const OptionsDialogTv = memo<Props>(({ navigation, route: { params } }) => {
  const toast = useTvToast();

  const [container, titleStyle] = useGlobalStyles(dialogStyles);

  const [title, items, index]: [string, (number | string)[], number] =
    useMemo(() => {
      const locale = localization.settings;
      switch (params.type) {
        case OptionsType.fontSize:
          const fontSizeValue = persistedStorage.getNumber('fontSize') ?? 1;
          return [
            locale.interfaceFontSize,
            Constants.fontSizeVariants.map(e => `${(e * 100).toFixed(0)}%`),
            Constants.fontSizeVariants.indexOf(fontSizeValue),
          ];
        case OptionsType.mouseMode:
          return [
            locale.interfaceMouseMode,
            [
              locale.autoMouseMode,
              locale.alwaysMouseMode,
              locale.neverMouseMode,
            ],
            persistedStorage.getNumber('mouseMode') ?? 0,
          ];
        case OptionsType.startScreen:
          return [
            locale.profileStartScreen,
            [locale.startScreenHome, locale.startScreenTv],
            persistedStorage.getNumber('startScreen') ?? 0,
          ];
        case OptionsType.tvStart:
          return [
            locale.profileTvStart,
            [
              locale.tvStartLastPosition,
              locale.tvStartLive,
              locale.tvStartNever,
            ],
            persistedStorage.getNumber('tvStart') ?? 0,
          ];
        case OptionsType.bufferLength:
          const bufferValue = States.mainState._settings?.http_caching.value;
          const bufferList = States.mainState._settings?.http_caching.list;
          if (bufferList === undefined || bufferValue === undefined) break;
          return [
            locale.streamBuffer,
            bufferList.map(e => `${e / 1000} ${localization.general.seconds}`),
            bufferList.indexOf(
              +(States.mainState._settings?.http_caching.value ?? 3000),
            ) ?? 0,
          ];
        case OptionsType.seekStep:
          const seekStepValue = persistedStorage.getNumber('seekStep') ?? 10;
          return [
            locale.interfaceSeekStep,
            Constants.seekStepVariants.map(
              e => `${e} ${localization.general.seconds}`,
            ),
            Constants.seekStepVariants.indexOf(seekStepValue),
          ];
        case OptionsType.tvBackButton:
          const backButtonValue =
            persistedStorage.getNumber('tvBackButton') ?? 0;
          return [
            locale.tvBackButton,
            [locale.tvBackPrevChannel, locale.tvBackChannelList],
            backButtonValue,
          ];
      }
      return ['', Utils.emptyArray, 0];
    }, [params.type]);

  const focusState = useFocusState(items.length, true, index);

  const up = useCallback((keyUp?: boolean) => {
    if (keyUp) return;
    focusState.decrement();
  }, []);
  const down = useCallback((keyUp?: boolean) => {
    if (keyUp) return;
    focusState.increment();
  }, []);
  const confirm = useMemo(() => {
    switch (params.type) {
      case OptionsType.fontSize:
        return () => {
          const i = focusState.focused;
          if (i !== undefined)
            persistedStorage.set('fontSize', Constants.fontSizeVariants[i]);
          navigation.pop();
        };
      case OptionsType.startScreen:
        return () => {
          const i = focusState.focused;
          if (i !== undefined) persistedStorage.set('startScreen', i);
          navigation.pop();
        };
      case OptionsType.tvStart:
        return () => {
          const i = focusState.focused;
          if (i !== undefined) persistedStorage.set('tvStart', i);
          navigation.pop();
        };
      case OptionsType.mouseMode:
        return () => {
          const i = focusState.focused;
          if (i !== undefined) persistedStorage.set('mouseMode', i);
          navigation.pop();
        };
      case OptionsType.bufferLength:
        return () => {
          const i = focusState.focused;
          if (i !== undefined) {
            const value = States.mainState._settings?.http_caching.list[i];
            if (value) {
              States.mainState.setBufferLength(value).catch(() => {
                toast?.show('warning', localization.error.settingNotSave);
              });
            }
          }
          navigation.pop();
        };
      case OptionsType.seekStep:
        return () => {
          const i = focusState.focused;
          if (i !== undefined)
            persistedStorage.set('seekStep', Constants.seekStepVariants[i]);
          navigation.pop();
        };
      case OptionsType.tvBackButton:
        return () => {
          const i = focusState.focused;
          if (i !== undefined) persistedStorage.set('tvBackButton', i);
          navigation.pop();
        };
    }
    return () => navigation.pop();
  }, [params.type]);
  const cancel = useCallback(() => navigation.pop(), []);

  const visibleItems = useMemo(
    () =>
      items.map((e, i) => (
        <OptionItem
          key={i}
          text={e}
          index={i}
          focusState={focusState}
          checked={i === index}
          onPress={confirm}
        />
      )),
    [index],
  );

  return (
    <View style={styles.container}>
      <TvPressable onPress={cancel} style={commonStyles.fill} />
      <KeyWrapper
        up={up}
        down={down}
        confirm={confirm}
        cancel={cancel}
        style={container}>
        <Text style={titleStyle}>{title}</Text>
        {visibleItems}
      </KeyWrapper>
    </View>
  );
});
OptionsDialogTv.displayName = 'OptionsDialogTv';

type ItemProps = {
  text: string | number;
  checked?: boolean;
  index: number;
  focusState: FocusStateType;
  onPress?: VoidCb;
};

const itemStyles = [
  'fr,ac,pv=12,ph=24',
  `fill,bg=${AppColors.mainBlue},br=12`,
  `hide,fill,bg=${AppColors.mainBlue},br=12`,
  'f=1,fs=32,fw=500,c=white,ml=16',
];

const OptionItem = observer<ItemProps>(
  ({ text, checked, index, focusState, onPress }) => {
    const focused = computed(() => focusState.focused === index).get();

    const [container, focusedContainer, unfocusedContainer, textStyle] =
      useGlobalStyles(itemStyles);

    const press = useCallback(() => {
      focusState.focused = index;
      onPress?.();
    }, [index, onPress]);

    return (
      <TvPressable onPress={press} style={container}>
        <View style={focused ? focusedContainer : unfocusedContainer} />
        <RadioButton checked={checked} focused={focused} />
        <Text style={textStyle}>{text}</Text>
      </TvPressable>
    );
  },
);

type RadioProps = {
  checked?: boolean;
  focused?: boolean;
};

const radioStyles = [
  'jc,ac,w=24,h=24,bw=1,bc=white,br=150',
  `jc,ac,w=24,h=24,bg=${AppColors.mainBlue},bw=1,bc=transparent,br=150`,
  'jc,ac,w=24,h=24,bg=white,bw=1,bc=transparent,br=150',
  'hide,w=8,h=8,bg=white,br=150',
  'w=8,h=8,bg=white,br=150',
  `w=8,h=8,bg=${AppColors.mainBlue},br=150`,
];

const RadioButton = memo<RadioProps>(({ checked, focused }) => {
  const [
    uncheckedContainer,
    checkedContainer,
    focusedContainer,
    uncheckedDot,
    checkedDot,
    focusedDot,
  ] = useGlobalStyles(radioStyles);

  const [containerStyle, dotStyle] = useMemo(() => {
    if (checked) {
      if (focused) return [focusedContainer, focusedDot];
      return [checkedContainer, checkedDot];
    }
    return [uncheckedContainer, uncheckedDot];
  }, [checked, focused]);

  return (
    <View style={containerStyle}>
      <View style={dotStyle} />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default OptionsDialogTv;
